import { useEffect, useRef } from "react";

const COLORS = ["#ffffff", "#00f5d4", "#8b5cf6", "#00d2ff", "#fbbf24"];

export default function Starfield({ density = 0.00014, className = "" }) {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = 0;
    let h = 0;
    let cx = 0;
    let cy = 0;
    let maxR = 1;
    let stars = [];
    let raf;
    let t = 0;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);

    const spawn = (initial) => {
      const angle = Math.random() * Math.PI * 2;
      const r = initial ? Math.random() * maxR : Math.random() * 30;
      return {
        angle,
        r,
        speed: 0.12 + Math.random() * 0.5,
        size: 0.4 + Math.random() * 1.5,
        phase: Math.random() * Math.PI * 2,
        tw: 0.4 + Math.random() * 1.4,
        color: COLORS[Math.random() < 0.84 ? 0 : 1 + Math.floor(Math.random() * (COLORS.length - 1))],
      };
    };

    const build = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = w * DPR;
      canvas.height = h * DPR;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      cx = w / 2;
      cy = h / 2;
      maxR = Math.hypot(w, h) / 2;
      const count = Math.min(420, Math.floor(w * h * density));
      stars = Array.from({ length: count }, () => spawn(true));
    };

    const onMove = (e) => {
      mouse.current.x = e.clientX / window.innerWidth;
      mouse.current.y = e.clientY / window.innerHeight;
    };

    const draw = () => {
      t += 0.016;
      ctx.clearRect(0, 0, w, h);
      const ox = (mouse.current.x - 0.5) * 26;
      const oy = (mouse.current.y - 0.5) * 26;
      for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        s.r += s.speed * (0.35 + (s.r / maxR) * 2.4);
        if (s.r > maxR) {
          stars[i] = spawn(false);
          continue;
        }
        const depth = s.r / maxR;
        const x = cx + Math.cos(s.angle) * s.r + ox * depth;
        const y = cy + Math.sin(s.angle) * s.r + oy * depth;
        const alpha =
          (0.2 + 0.8 * Math.abs(Math.sin(t * s.tw + s.phase))) * Math.min(1, s.r / 70);
        ctx.globalAlpha = alpha;
        ctx.fillStyle = s.color;
        ctx.beginPath();
        ctx.arc(x, y, s.size * (0.5 + depth), 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };

    build();
    draw();
    window.addEventListener("resize", build);
    window.addEventListener("mousemove", onMove);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", build);
      window.removeEventListener("mousemove", onMove);
    };
  }, [density]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="starfield-canvas"
      className={`absolute inset-0 ${className}`}
      aria-hidden="true"
    />
  );
}
